import axios from "axios";

// Webhook REST dedicado só pra essa ação (criar contato/deal de auditoria antes
// de abrir o chat de suporte) - token restrito a esse escopo, não precisa de
// proteção especial.
const BITRIX_WEBHOOK_URL =
    "https://clubdacotacao.bitrix24.com.br/rest/1012/lfuzgpndtztr9sb8/";

const http = axios.create({
    baseURL: BITRIX_WEBHOOK_URL,
    timeout: 15000,
});

function buildComments({ name, email, phone, company, grupo, cliId, userId }) {
    return [
        name && `Nome: ${name}`,
        email && `E-mail: ${email}`,
        phone && `Telefone: ${phone}`,
        company && `Empresa: ${company}`,
        grupo && `Grupo: ${grupo}`,
        cliId && `Código do cliente: ${cliId}`,
        userId && `Código do usuário: ${userId}`,
    ]
        .filter(Boolean)
        .join("\n");
}

function findContactId({ email, phone }) {
    const lookup = (type, values) => {
        if (!values.length) return Promise.resolve(null);
        // POST (não GET com `params`) - o Bitrix espera filtros/arrays em
        // notação de colchetes na query string, e o axios serializa objetos
        // aninhados como JSON.stringify, o que o Bitrix rejeita. Todo método
        // REST do Bitrix aceita ser chamado via POST com corpo JSON, então é
        // mais simples e seguro sempre postar o corpo puro.
        return http
            .post("crm.duplicate.findbycomm", { entity_type: "CONTACT", type, values })
            .then((response) => {
                const ids =
                    (response.data &&
                        response.data.result &&
                        response.data.result.CONTACT) ||
                    [];
                return ids.length ? ids[0] : null;
            })
            .catch(() => null);
    };

    return lookup("EMAIL", email ? [email] : []).then((contactId) => {
        if (contactId) return contactId;
        return lookup("PHONE", phone ? [phone] : []);
    });
}

function findCompanyId({ company }) {
    if (!company) return Promise.resolve(null);

    // POST pelo mesmo motivo de `findContactId` acima (evita o filter/array
    // virar JSON.stringify na query string).
    return http
        .post("crm.company.list", { filter: { TITLE: company }, select: ["ID"] })
        .then((response) => {
            const items = (response.data && response.data.result) || [];
            return items.length ? items[0].ID : null;
        })
        .catch(() => null);
}

function createCompany({ company }) {
    return http
        .post("crm.company.add", { fields: { TITLE: company } })
        .then((response) => response.data && response.data.result);
}

function findOrCreateCompanyId({ company }) {
    if (!company) return Promise.resolve(null);

    return findCompanyId({ company }).then((existingCompanyId) => {
        if (existingCompanyId) return existingCompanyId;
        return createCompany({ company }).catch(() => null);
    });
}

function createContact({ name, email, phone, company, companyId }) {
    const fields = { NAME: name || "Visitante" };
    if (email) fields.EMAIL = [{ VALUE: email, VALUE_TYPE: "WORK" }];
    if (phone) fields.PHONE = [{ VALUE: phone, VALUE_TYPE: "WORK" }];
    if (company) fields.COMPANY_TITLE = company;
    if (companyId) fields.COMPANY_ID = companyId;

    return http
        .post("crm.contact.add", { fields })
        .then((response) => response.data && response.data.result);
}

// Funil (categoria) dedicado aos Deals criados a partir do chat de suporte.
const DEAL_CATEGORY_ID = 10;

// Reaproveita um Deal já aberto pra esse contato nesse funil, em vez de criar
// um novo - cobre o caso de recarregar a página (o cache em memória do
// App.vue se perde no reload) e clicar em suporte de novo.
function findOpenDealId({ contactId }) {
    if (!contactId) return Promise.resolve(null);

    return http
        .post("crm.deal.list", {
            filter: {
                CATEGORY_ID: DEAL_CATEGORY_ID,
                CONTACT_ID: contactId,
                CLOSED: "N",
            },
            select: ["ID"],
            order: { ID: "DESC" },
        })
        .then((response) => {
            const items = (response.data && response.data.result) || [];
            return items.length ? items[0].ID : null;
        })
        .catch(() => null);
}

function createDeal({ contactId, companyId, title, comments }) {
    const fields = {
        TITLE: title,
        CATEGORY_ID: DEAL_CATEGORY_ID,
        OPENED: "Y",
        COMMENTS: comments,
    };
    if (contactId) fields.CONTACT_IDS = [contactId];
    if (companyId) fields.COMPANY_ID = companyId;

    return http
        .post("crm.deal.add", { fields })
        .then((response) => response.data && response.data.result);
}

/**
 * Cria (ou reaproveita) um Contato no CRM do Bitrix24 e abre um Deal vinculado
 * a ele, pra servir de registro de auditoria antes do chat de suporte abrir.
 * Nunca rejeita - qualquer erro é engolido e logado, já que a falha aqui não
 * pode impedir o chat de abrir.
 *
 * @param {{ userId, name, email, phone, company, cliId, grupo }} userData
 * @returns {Promise<{ dealId: string|number, dealUrl: string } | null>}
 */
export function createBitrixDeal(userData) {
    const { name, email, phone, company } = userData;

    return findOrCreateCompanyId({ company })
        .then((companyId) =>
            findContactId({ email, phone })
                .then((existingContactId) => {
                    if (existingContactId) return existingContactId;
                    return createContact({
                        name,
                        email,
                        phone,
                        company,
                        companyId,
                    }).catch(() => null);
                })
                .then((contactId) =>
                    findOpenDealId({ contactId }).then((existingDealId) => {
                        if (existingDealId) return existingDealId;

                        const title = `Chat - ${name || "Visitante"}${
                            company ? ` (${company})` : ""
                        }`;
                        const comments = buildComments(userData);
                        return createDeal({
                            contactId,
                            companyId,
                            title,
                            comments,
                        });
                    })
                )
        )
        .then((dealId) => {
            if (!dealId) return null;
            return {
                dealId,
                dealUrl: `${
                    new URL(BITRIX_WEBHOOK_URL).origin
                }/crm/deal/details/${dealId}/`,
            };
        })
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.error("Falha ao criar Deal no Bitrix24:", error);
            return null;
        });
}
